import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import { ColorScheme, colorSchemeToCss } from 'owa-color-utils/lib/ColorScheme';
import { default as FreeBusyConstants, FreeBusyType } from '../schema/FreeBusyType';
import { getPaletteAsRawColors } from 'owa-theme';
import { isFeatureEnabled } from 'owa-feature-flags';

let styles = require('./freeBusyStyles.scss');
let classNames = require('classnames/bind').bind(styles);

/**
 * Gets the css class for the given free busy status to be positioned and styled for a specific item
 * @param freeBusyStatus The free busy status
 */
export function getFreeBusyItemBarClass(
    freeBusyStatus: FreeBusyType,
    includePositioningClass: boolean = true
) {
    return classNames(getFreeBusyStripClass(freeBusyStatus), { freeBusy: includePositioningClass });

    // for OOF case - please refer to the generateFreeBusyStyling() function below
}

/**
 * Gets the css class for the busy strip for the given free busy status
 * @param freeBusyStatus The free busy status
 */
export function getFreeBusySurfaceStripClass(freeBusyStatus: FreeBusyType) {
    return classNames(styles.surfaceBusyBar, getFreeBusyItemBarClass(freeBusyStatus));
}

/**
 * Gets the css class for the busy strip, not necessarily on an item
 * @param freeBusyStatus The free busy status
 */
export function getFreeBusyStripClass(freeBusyStatus: FreeBusyType) {
    switch (freeBusyStatus) {
        case FreeBusyConstants.OOF:
            return styles.awayItemBar;
        case FreeBusyConstants.Tentative:
            return styles.tentativeItemBar;
        case FreeBusyConstants.WorkingElsewhere:
            return styles.workingElsewhereItemBar;
        case FreeBusyConstants.Free:
            return styles.calendarFree;
        case FreeBusyConstants.Busy:
            return styles.busyItemBar;
        case FreeBusyConstants.NoData:
        case FreeBusyConstants.Loading:
        case FreeBusyConstants.NoInformation:
            return styles.noInformationBar;
    }
}

/**
 * Generates the item styling based on free busy status. Used for setting opacity on tentative items
 * @param freeBusyStatus The free busy status
 */
export function getFreeBusyItemWrapperClass(freeBusyStatus: FreeBusyType) {
    switch (freeBusyStatus) {
        case FreeBusyConstants.WorkingElsewhere:
            return styles.workingElsewhereItem;
        case FreeBusyConstants.Tentative:
            return styles.tentativeItem;
        case FreeBusyConstants.Free:
            return styles.freeItem;
        default:
            return null;
    }
}

/**
 * Generates the fixed styles for the given item properties
 * @param freeBusyStatus The free busy status
 * @param colorScheme The calendar color scheme
 * @param duration the duration in milliseconds
 */
export function getFreeBusyItemBarStyling(
    freeBusyStatus: FreeBusyType,
    colorScheme: ColorScheme,
    duration?: number
) {
    return generateFreeBusyStyling(
        freeBusyStatus,
        colorScheme,
        isNaN(duration) ? TimeConstants.MillisecondsIn1Hour : duration
    );
}

/**
 * Generates the fixed styles for the given item properties of an all day free busy strip, using default styling
 * @param freeBusyStatus The free busy status
 * @param colorScheme The calendar color scheme
 */
export function getFreeBusySurfaceStripStyling(
    freeBusyStatus: FreeBusyType,
    colorScheme: ColorScheme
) {
    let styles = generateFreeBusyStyling(
        freeBusyStatus,
        colorScheme,
        TimeConstants.MillisecondsIn1Hour /* default time for styling */
    );
    // the all day strip shouldn't have a height setting or the bars overflow the surface
    styles.height = 'initial';
    return styles;
}

function generateFreeBusyStyling(
    freeBusyStatus: FreeBusyType,
    colorScheme: ColorScheme,
    duration: number
) {
    let colorCssNormal = colorSchemeToCss(colorScheme);
    let rawPalette = getPaletteAsRawColors();

    const noInformationGray = rawPalette.neutralSecondary;
    let awayPurple = '#c21cc1';

    let returnBlob = {
        backgroundColor: colorCssNormal.backgroundColor,
        borderColor: colorCssNormal.backgroundColor,
        backgroundImage: null,
        height: null,
    };

    const minimumDuration = isFeatureEnabled('cal-mf-shortenEventBoxSurface')
        ? TimeConstants.MillisecondsIn15Mins
        : TimeConstants.MillisecondsIn30Mins;

    // ultrashort items take up same space as a 15-min or 30-min item, but show the real length with the free-busy bar being shortened
    if (duration < minimumDuration) {
        returnBlob.height = (duration / minimumDuration) * 100 + '%';
    }

    // colors for specific freebusy statuses
    switch (freeBusyStatus) {
        case FreeBusyConstants.Free:
            returnBlob.borderColor = colorCssNormal.backgroundColor;
            returnBlob.backgroundColor = 'transparent';
            break;
        case FreeBusyConstants.OOF:
            returnBlob.borderColor = awayPurple;
            returnBlob.backgroundColor = awayPurple;
            break;
        case FreeBusyConstants.NoInformation:
            returnBlob.borderColor = noInformationGray;
            returnBlob.backgroundColor = noInformationGray;
            break;
    }

    return returnBlob;
}
